import { useQuery, useQueryClient } from "@tanstack/react-query";
import { pageApi } from "../services/api";
import staticBundle from "../data/bundle.json";

// Query keys factory for consistent key management
export const queryKeys = {
  bundle: ["bundle"] as const,
  pages: ["pages"] as const,
  page: (slug: string) => ["pages", slug] as const,
};

// Hook to get the static website bundle (no API call - instant load)
export function useBundle() {
  return useQuery({
    queryKey: queryKeys.bundle,
    queryFn: () => Promise.resolve(staticBundle),
    staleTime: Infinity, // Never refetch - it's static
    gcTime: Infinity, // Keep in cache forever
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

// Hook to fetch all pages (legacy - use useBundle instead)
export function usePages() {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: queryKeys.pages,
    queryFn: async () => {
      // Try to get pages from bundle first
      const bundleData = queryClient.getQueryData(queryKeys.bundle) as any;
      if (bundleData?.pages) {
        return bundleData.pages;
      }
      // Fallback to individual API call
      return pageApi.getAllPages();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to fetch a specific page with intelligent caching
export function usePage(slug: string) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: queryKeys.page(slug),
    queryFn: async () => {
      // Try to get page from bundle first
      const bundleData = queryClient.getQueryData(queryKeys.bundle) as any;
      if (bundleData?.pages) {
        const page = bundleData.pages.find((p: any) => p.slug === slug);
        if (page) return page;
      }
      // Fallback to individual API call
      console.warn(`⚠️ Page "${slug}" not found in bundle, fetching individually`);
      return pageApi.getPageBySlug(slug);
    },
    // Use initialData from cache if available (prevents loading state)
    initialData: () => {
      const cachedPage = queryClient.getQueryData(queryKeys.page(slug));
      if (cachedPage) return cachedPage;
      
      // Try to get from bundle
      const bundleData = queryClient.getQueryData(queryKeys.bundle) as any;
      if (bundleData?.pages) {
        return bundleData.pages.find((p: any) => p.slug === slug);
      }
      return undefined;
    },
    enabled: !!slug, // Only run if slug exists
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

// Hook to prefetch all pages for instant navigation (now uses bundle)
export function usePrefetchPages() {
  const queryClient = useQueryClient();

  const prefetchAllPages = async () => {
    try {
      // Fetch the complete bundle in one request
      const bundle = await pageApi.getBundle();
      
      // Cache the bundle
      queryClient.setQueryData(queryKeys.bundle, bundle);
      
      // Cache individual pages for direct access
      bundle.pages.forEach((page) => {
        queryClient.setQueryData(queryKeys.page(page.slug), page);
      });
      
      console.log(`✅ Loaded bundle with ${bundle.pages.length} pages in a single request`);
    } catch (error) {
      console.error("Failed to prefetch bundle:", error);
    }
  };

  // Debug function to check cache status
  const checkCacheStatus = (slug: string) => {
    const bundleData = queryClient.getQueryData(queryKeys.bundle);
    const pageData = queryClient.getQueryData(queryKeys.page(slug));
    console.log(`Cache status for "${slug}":`, {
      bundleData: !!bundleData,
      pageData: !!pageData,
    });
    return { bundleData: !!bundleData, pageData: !!pageData };
  };

  return { prefetchAllPages, checkCacheStatus };
}

